import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';

import clientReviewService from '../../services/clientReviewService';
import FeaturedTestimonialHero from './components/FeaturedTestimonialHero';
import TestimonialShowcaseGrid from './components/TestimonialShowcaseGrid';
import TestimonialFilterPanel from './components/TestimonialFilterPanel';
import TestimonialDetailModal from './components/TestimonialDetailModal';

const PublicTestimonialShowcasePortal = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    departmentType: 'all',
    organizationSize: 'all',
    useCase: 'all',
    testimonialType: 'all',
    minRating: 0,
    searchTerm: ''
  });

  const handleGlobalFilterChange = (globalFilters) => {
    console.log('Global filters changed:', globalFilters);
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [testimonials, filters]);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await clientReviewService?.getPublishedReviews();
      setTestimonials(data);
    } catch (err) {
      setError(err?.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...testimonials];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(t =>
        t?.companyName?.toLowerCase()?.includes(term) ||
        t?.reviewerName?.toLowerCase()?.includes(term) ||
        t?.reviewText?.toLowerCase()?.includes(term)
      );
    }

    // Department type filter
    if (filters?.departmentType !== 'all') {
      filtered = filtered?.filter(t => t?.departmentType === filters?.departmentType);
    }

    // Organization size filter
    if (filters?.organizationSize !== 'all') {
      filtered = filtered?.filter(t => t?.organizationSize === filters?.organizationSize);
    }

    // Use case filter
    if (filters?.useCase !== 'all') {
      filtered = filtered?.filter(t => t?.useCase === filters?.useCase);
    }

    // Testimonial type filter
    if (filters?.testimonialType !== 'all') {
      filtered = filtered?.filter(t => t?.testimonialType === filters?.testimonialType);
    }

    // Minimum rating filter
    if (filters?.minRating > 0) {
      filtered = filtered?.filter(t => t?.starRating >= filters?.minRating);
    }

    setFilteredTestimonials(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleViewDetails = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedTestimonial(null);
  };

  const featuredTestimonials = testimonials?.filter(t => t?.isFeatured) || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />

      <main className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Public Testimonial Showcase Portal
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Verified client reviews and success stories from PNG organizations
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator onRefresh={loadTestimonials} />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Featured Testimonial Hero */}
        {featuredTestimonials?.length > 0 && (
          <div className="mb-6">
            <FeaturedTestimonialHero testimonials={featuredTestimonials} />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Filter Panel - 4 cols */}
          <div className="col-span-12 lg:col-span-4">
            <TestimonialFilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
              totalCount={filteredTestimonials?.length}
            />
          </div>

          {/* Testimonials Grid - 8 cols */}
          <div className="col-span-12 lg:col-span-8">
            {loading ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading testimonials...</p>
              </div>
            ) : (
              <TestimonialShowcaseGrid 
                testimonials={filteredTestimonials}
                onViewDetails={handleViewDetails}
              />
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedTestimonial && (
        <TestimonialDetailModal 
          testimonial={selectedTestimonial}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default PublicTestimonialShowcasePortal;